import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { BackgroundBeams } from '../components/BackgroundBeams';
import {
  FaRocket,
  FaUsers,
  FaAward,
  FaGlobeAmericas,
  FaLinkedinIn,
  FaEnvelope,
  FaArrowRight,
  FaChartLine,
  FaLightbulb,
  FaHandshake,
  FaWhatsapp,
  FaTimes
} from 'react-icons/fa';
import '../pages/AboutPage.css';
import BhishmangImage from '../assets/team/bhishmang.jpeg'
import PrerakImage from '../assets/team/prerak.jpeg'
import ParthImage from '../assets/team/parth.jpeg'

// Enhanced team members data
const teamMembers = [
  {
    name: 'Bhishmang Patel',
    role: 'Co-Founder, CEO and CTO',
    bio: 'Visionary leader with 3+ years in tech innovation.',
    image: BhishmangImage,
    skills: ['Leadership', 'Development', 'Strategy', 'AI', 'Finance'],
    social: { linkedin: 'https://www.linkedin.com/in/bhishmang-patel', email: 'bhishmangpatel157@gmail.com' },
    portfolio: 'https://bhishmang-patel.vercel.app/'
  },

  {
    name: 'Parth Patel',
    role: 'Founder, COO and CCO',
    bio: 'Expert in Cyber security and Application security.',
    image: ParthImage,
    skills: ['Development', 'AI', 'Cloud', 'Marketing', 'Finance'],
    social: { linkedin: 'https://www.linkedin.com/in/parthpateldotin?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app', email: '#' },
    portfolio: 'https://parthpatel.in/'
  },

  {
    name: 'Prerak Patel',
    role: 'Co-Founder, CMO and CFO',
    bio: 'Expert in software architecture and development.',
    image: PrerakImage,
    skills: ['Development', 'Architecture', 'Cloud', 'Finance'],
    social: { linkedin: 'https://www.linkedin.com/in/prerak-patel-60a908219/', email: 'patelprerak435@gmail.com' },
    portfolio: 'https://prerak.deployx.in/'
  },

];

// Enhanced achievements with stats
const stats = [
  { number: '50+', label: 'Projects Completed', icon: <FaRocket /> },
  { number: '98%', label: 'Client Satisfaction', icon: <FaUsers /> },
  { number: '10+', label: 'Industry Awards', icon: <FaAward /> },
  { number: '10+', label: 'Countries Served', icon: <FaGlobeAmericas /> }
];

// Core values
const values = [
  {
    icon: <FaLightbulb />,
    title: 'Innovation',
    description: 'Pushing boundaries with cutting-edge solutions'
  },
  {
    icon: <FaHandshake />,
    title: 'Integrity',
    description: 'Building trust through transparency and honesty'
  },
  {
    icon: <FaChartLine />,
    title: 'Excellence',
    description: 'Delivering quality in every project we undertake'
  }
];

const AboutUs = () => {
  const [hoveredMember, setHoveredMember] = useState(null);
  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);

  // WhatsApp configuration
  const whatsappNumber = '+916350462627';
  const whatsappMessage = 'Hi! I would like to discuss a project with DEPLOY-X.';
  const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  // Auto-show tooltip after 3 seconds
  React.useEffect(() => {
    const tooltipTimer = setTimeout(() => {
      setShowWhatsAppTooltip(true);
      // Auto-hide after 5 seconds
      setTimeout(() => setShowWhatsAppTooltip(false), 5000);
    }, 3000);
    return () => clearTimeout(tooltipTimer);
  }, []);

  return (
    <motion.div
      className="about-us-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <SEO
        title="About DEPLOY-X | Our Story & Team"
        description="Meet the passionate team behind DEPLOY-X. Learn about our mission to transform businesses through AI-powered digital solutions."
        pathname="/about"
        keywords={['about deploy-x', 'team', 'company story', 'digital agency India']}
      />
      <Navbar />
      <main>
        {/* Hero Section with Gradient Overlay */}
        <section className="about-hero">
          <BackgroundBeams />
          <div className="hero-overlay"></div>
          <div className="container">
            <motion.div
              className="about-hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.span
                className="hero-badge"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Since 2025
              </motion.span>
              <h1>Transforming Ideas Into <span className="gradient-text">Digital Reality</span></h1>
              <p>
                We're a passionate team of innovators, designers, and developers committed to delivering exceptional digital experiences that drive business growth.
              </p>
              <div className="hero-stats">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    className="stat-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  >
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Story Section - Split Layout */}
        <section className="story-section">
          <div className="container">
            <div className="story-grid">
              <motion.div
                className="story-content"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <span className="section-label">Our Journey</span>
                <h2>Building the Future, <span>One Project at a Time</span></h2>
                <p>
                  Founded in 2025, DEPLOY-X emerged from a vision to bridge the gap between technology and business needs. What started as a small team of passionate experts has grown into a global leader in digital innovation.
                </p>
                <p>
                  Today, we partner with businesses across multiple continents, delivering AI-powered solutions, cutting-edge web applications, and transformative mobile experiences that drive real results.
                </p>
                <div className="story-highlights">
                  <div className="highlight-item">
                    <FaRocket className="highlight-icon" />
                    <div>
                      <h4>Mission-Driven</h4>
                      <p>Empowering businesses through technology</p>
                    </div>
                  </div>
                  <div className="highlight-item">
                    <FaAward className="highlight-icon" />
                    <div>
                      <h4>Award-Winning</h4>
                      <p>Recognized for innovation and excellence</p>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="story-visual"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="visual-card card-1">
                  <div className="card-icon"><FaLightbulb /></div>
                  <h4>Innovation First</h4>
                  <p>Pioneering solutions for tomorrow's challenges</p>
                </div>
                <div className="visual-card card-2">
                  <div className="card-icon"><FaUsers /></div>
                  <h4>Client-Centric</h4>
                  <p>Your success is our success</p>
                </div>
                <div className="visual-card card-3">
                  <div className="card-icon"><FaGlobeAmericas /></div>
                  <h4>Global Impact</h4>
                  <p>Making a difference worldwide</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="why-choose-us-section">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Why DEPLOY-X</span>
              <h2>Why Choose Us</h2>
              <p>Discover what makes us the perfect partner for your digital transformation</p>
            </motion.div>

            <div className="why-choose-grid">
              <motion.div
                className="why-choose-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="why-icon">
                  <FaRocket />
                </div>
                <h3>Cutting-Edge Technology</h3>
                <p>We leverage the latest technologies and industry best practices to deliver innovative solutions that keep you ahead of the competition.</p>
              </motion.div>

              <motion.div
                className="why-choose-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="why-icon">
                  <FaUsers />
                </div>
                <h3>Expert Team</h3>
                <p>Our team of seasoned professionals brings years of experience and expertise to every project, ensuring top-quality results.</p>
              </motion.div>

              <motion.div
                className="why-choose-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="why-icon">
                  <FaAward />
                </div>
                <h3>Proven Track Record</h3>
                <p>With 50+ successful projects and 98% client satisfaction, our results speak for themselves. We deliver excellence every time.</p>
              </motion.div>

              <motion.div
                className="why-choose-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="why-icon">
                  <FaLightbulb />
                </div>
                <h3>Innovative Solutions</h3>
                <p>We don't just follow trends, we create them. Our innovative approach ensures your business stays ahead in the digital landscape.</p>
              </motion.div>

              <motion.div
                className="why-choose-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="why-icon">
                  <FaHandshake />
                </div>
                <h3>Client-Centric Approach</h3>
                <p>Your success is our priority. We work closely with you throughout the journey, ensuring transparency and collaboration at every step.</p>
              </motion.div>

              <motion.div
                className="why-choose-item"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -8 }}
              >
                <div className="why-icon">
                  <FaGlobeAmericas />
                </div>
                <h3>Global Reach</h3>
                <p>Serving clients across 30+ countries, we bring a global perspective with local expertise to deliver world-class solutions.</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="section-label">What Drives Us</span>
              <h2>Our Core Values</h2>
              <p>The principles that guide everything we do</p>
            </motion.div>
            <div className="values-grid">
              {values.map((value, index) => (
                <motion.div
                  className="value-card"
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section - Modern Grid */}
        <section className="team-section">
          <div className="container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="section-label">The Team</span>
              <h2>Meet the Minds Behind <span className='logo-title' >DEPLOY - <span>X</span></span></h2>
              <p>Talented individuals united by a passion for innovation</p>
            </motion.div>

            <div className="team-grid">
              {teamMembers.map((member, index) => (
                <motion.div
                  className="team-card"
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  onHoverStart={() => setHoveredMember(index)}
                  onHoverEnd={() => setHoveredMember(null)}
                >
                  {/* Card Inner */}
                  <div className="team-card-inner">
                    {/* Image Section */}
                    <div className="member-image-section">
                      <div className="member-image-wrapper">
                        <motion.img
                          src={member.image}
                          alt={member.name}
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.4 }}
                        />
                        <div className="image-gradient"></div>
                      </div>

                      {/* Floating Social Links */}
                      <motion.div
                        className="floating-social"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                          opacity: hoveredMember === index ? 1 : 0,
                          y: hoveredMember === index ? 0 : 20
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <a href={member.social.linkedin} className="social-icon">
                          <FaLinkedinIn />
                        </a>
                        <a href={`mailto:${member.social.email}`} className="social-icon">
                          <FaEnvelope />
                        </a>
                      </motion.div>
                    </div>

                    {/* Content Section */}
                    <div className="member-content">
                      <div className="member-header">
                        <div>
                          <h3>{member.name}</h3>
                          <span className="member-role">{member.role}</span>
                        </div>
                        <motion.div
                          className="member-index"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </motion.div>
                      </div>

                      <p className="member-bio">{member.bio}</p>

                      <div className="member-skills">
                        {member.skills.map((skill, idx) => (
                          <motion.span
                            key={idx}
                            className="skill-badge"
                            whileHover={{ scale: 1.1 }}
                          >
                            {skill}
                          </motion.span>
                        ))}
                      </div>

                      <motion.a
                        href={member.portfolio}
                        className="contact-member"
                        whileHover={{ x: 5 }}
                      >
                        Get in touch <FaArrowRight />
                      </motion.a>
                    </div>
                  </div>

                  {/* Decorative Element */}
                  <div className="card-decoration"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - Enhanced */}
        <section className="cta-section">
          <div className="container">
            <motion.div
              className="cta-wrapper"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="cta-content">
                <h2>Let's Build Something Amazing Together</h2>
                <p>Ready to transform your ideas into reality? Our team is here to help you succeed.</p>
                <div className="cta-buttons">
                  <a href="/contact" className="btn-primary">Start Your Project</a>
                  <a href="/services" className="btn-secondary">Explore Services</a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />

      {/* WhatsApp Floating Button */}
      <div className="whatsapp-float-container">
        <AnimatePresence>
          {showWhatsAppTooltip && (
            <motion.div
              className="whatsapp-tooltip"
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <button
                className="tooltip-close"
                onClick={() => setShowWhatsAppTooltip(false)}
                aria-label="Close tooltip"
              >
                <FaTimes />
              </button>
              <p className="tooltip-text">
                <strong>Need Help?</strong><br />
                Chat with us on WhatsApp!
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.a
          href={whatsappURL}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-button"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
            delay: 1
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onMouseEnter={() => setShowWhatsAppTooltip(true)}
          onMouseLeave={() => setShowWhatsAppTooltip(false)}
          aria-label="Chat on WhatsApp"
        >
          <FaWhatsapp className="whatsapp-icon" />
          <span className="pulse-ring"></span>
          <span className="pulse-ring pulse-ring-delay"></span>
        </motion.a>
      </div>
    </motion.div>
  );
};

export default AboutUs;